import { Controller, useForm } from "react-hook-form";
import { FilterLayout } from "../layout/FilterLayout";
import { Autocomplete, FormControl, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import {
  getAllProjects,
  getShiftTypes,
  getUserProjects,
} from "../../services/timesheet";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Button from "../ui/Button";
import { formatDateToYYYYMMDD } from "../../Helper/functionHelper";
import useAuthUser from "../../hooks/useAuthUser";

const dayOptions = [
  { label: "Week Day", value: "WD" },
  { label: "Week Off", value: "WO" },
  { label: "Holiday", value: "HD" },
];

const timesheetStatusOptions = [
  { label: "Pending", value: "0" },
  { label: "Approved", value: "1" },
  { label: "Rejected", value: "2" },
];

const compOffStatusOptions = [
  { label: "Pending", value: "0" },
  { label: "Approved", value: "1" },
  { label: "Rejected", value: "2" },
];

const userStatusOptions = [
  { label: "Active", value: "1" },
  { label: "Inactive", value: "0" },
];

const TimesheetFilter = ({
  onFilter,
  exportTimesheets,
  defaultReportingManager = null,
  reportingManagers = [],
}) => {
  const { isAdminHrRole, isManagerRole, isEmployeeRole } = useAuthUser();
  const { control, handleSubmit, reset, getValues, setValue, watch } = useForm({
    defaultValues: {
      search: "",
      shift_type: "",
      timesheet_day: "",
      project: "",
      from_date: "",
      to_date: "",
      status: "",
      comp_off_status: "",
      reporting_manager: "",
      user_status: "1",
    },
  });
  const fromDate = watch("from_date");
  const toDate = watch("to_date");

  const [shiftTypes, setShiftTypes] = useState([]);
  const [projects, setProjects] = useState([]);

  const fetchData = async () => {
    try {
      const fetchArray = [getShiftTypes()];

      if (isAdminHrRole) {
        fetchArray.push(getAllProjects());
      } else if (isManagerRole) {
        fetchArray.push(getAllProjects());
      } else if (isEmployeeRole) {
        fetchArray.push(getUserProjects());
      }

      const [shiftRes, projectsRes] = await Promise.all(fetchArray);
      if (shiftRes.status === 200) {
        setShiftTypes(shiftRes.data.data.shiftTypeData);
      } else {
        console.error("Failed to fetch shift types", shiftRes);
        setShiftTypes([]);
      }
      if (projectsRes.status === 200) {
        if (isEmployeeRole) {
          setProjects(projectsRes.data.data.userProjects);
        } else {
          setProjects(projectsRes.data.data.projects);
        }
      } else {
        console.error("Failed to fetch projects", projectsRes);
        setProjects([]);
      }
    } catch (error) {
      console.error("An error occurred while fetching data", error);
      setShiftTypes([]);
      setProjects([]);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (defaultReportingManager) {
      setValue("reporting_manager", defaultReportingManager);
    }
  }, [defaultReportingManager, setValue]);

  const onSubmit = (data) => {
    const searchParams = {};

    if (data?.search) searchParams.search = data.search.trim();
    if (data?.shift_type) searchParams["shift_type"] = data.shift_type;
    if (data?.timesheet_day) searchParams["timesheet_day"] = data.timesheet_day;
    if (data?.project !== undefined && data?.project !== null) searchParams.project = data.project;
    if (data?.from_date)
      searchParams["from_date"] = formatDateToYYYYMMDD(data.from_date);
    if (data?.to_date)
      searchParams["to_date"] = formatDateToYYYYMMDD(data.to_date);

    if (data?.status) searchParams.status = data.status;
    if (data?.comp_off_status)
      searchParams["comp_off_status"] = data.comp_off_status;
    if (data?.reporting_manager) {
      searchParams.reporting_manager = data.reporting_manager._UserID;
    }
    if (data?.user_status) searchParams.user_status = data.user_status;
    onFilter(searchParams);
  };

  const handleClear = () => {
    reset();
    onFilter();
  };
  const handleExport = () => {
    const searchParams = {};
    const formValues = getValues();
    if (formValues?.search) searchParams.search = formValues.search;
    if (formValues?.shift_type)
      searchParams["shift_type"] = formValues.shift_type;
    if (formValues?.timesheet_day)
      searchParams["timesheet_day"] = formValues.timesheet_day;
    if (formValues?.project !== undefined && formValues?.project !== null) searchParams.project = formValues.project;
    if (formValues?.from_date)
      searchParams["from_date"] = formatDateToYYYYMMDD(formValues.from_date);
    if (formValues?.to_date)
      searchParams["to_date"] = formatDateToYYYYMMDD(formValues.to_date);

    if (formValues?.status) searchParams.status = formValues.status;
    if (formValues?.comp_off_status)
      searchParams["comp_off_status"] = formValues.comp_off_status;
    if (formValues?.reporting_manager) {
      searchParams.reporting_manager = formValues.reporting_manager._UserID;
    }
    if (formValues?.user_status)
      searchParams.user_status = formValues.user_status;

    searchParams.export = true;
    exportTimesheets(searchParams);
  };

  const searchLabel = isEmployeeRole
    ? "Description / Project"
    : "Emp Name / Code / Description / Project";

  return (
    <FilterLayout>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          {/* Text Input */}
          <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
            <Controller
              name="search"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={searchLabel}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    backgroundColor: "white",
                    marginTop: "8px",
                    marginBottom: "8px",
                    height: "40px",
                    ".MuiInputBase-input": { padding: "10px 6px" },
                  }}
                />
              )}
            />
          </div>

          {/* Shift Type Select */}
          <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
            <Controller
              name="shift_type"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    backgroundColor: "white",
                    marginTop: "8px",
                    // marginBottom: "8px",
                  }}
                >
                  <Autocomplete
                    {...field}
                    options={shiftTypes}
                    getOptionLabel={(option) =>
                      typeof option === "string"
                        ? option
                        : option.shiftTypeTitle || ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.shiftTypeId === value?.shiftTypeId
                    }
                    value={
                      shiftTypes.find(
                        (item) => item.shiftTypeId === field.value
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      field.onChange(newValue ? newValue.shiftTypeId : "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Shift Type"
                        size="small"
                        sx={{
                          "& .MuiInputBase-root": {
                            height: 40,
                          },
                        }}
                      />
                    )}
                  />
                </FormControl>
              )}
            />
          </div>

          {/* Timesheet Day Select */}
          <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
            <Controller
              name="timesheet_day"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    backgroundColor: "white",
                    marginTop: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <Autocomplete
                    {...field}
                    options={dayOptions}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option.label
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.value === value?.value
                    }
                    value={
                      dayOptions.find((item) => item.value === field.value) ||
                      null
                    }
                    onChange={(event, newValue) => {
                      field.onChange(newValue ? newValue.value : "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Day Type"
                        size="small"
                        sx={{
                          "& .MuiInputBase-root": {
                            height: 40,
                          },
                        }}
                      />
                    )}
                  />
                </FormControl>
              )}
            />
          </div>

          {/* Project Select */}
          <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
            <Controller
              name="project"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    backgroundColor: "white",
                    marginTop: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <Autocomplete
                    {...field}
                    options={projects}
                    getOptionLabel={(option) =>
                      typeof option === "string"
                        ? option
                        : option.projectName || ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.projectId === value?.projectId
                    }
                    value={
                      projects.find((item) => item.projectId === field.value) ||
                      null
                    }
                    onChange={(event, newValue) => {
                      field.onChange(newValue ? newValue.projectId : "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Project"
                        size="small"
                        sx={{
                          "& .MuiInputBase-root": {
                            height: 40,
                          },
                        }}
                      />
                    )}
                  />
                </FormControl>
              )}
            />
          </div>

          {/* From Date DatePicker */}
          <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
            <Controller
              name="from_date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="From Date"
                  {...field}
                  onChange={field.onChange}
                  value={field.value || null}
                  format="dd MMM yyyy"
                  maxDate={toDate || null}
                  slotProps={{
                    textField: {
                      variant: "outlined",
                      fullWidth: true,
                      size: "small",
                      sx: {
                        backgroundColor: "white",
                        marginTop: "8px",
                        marginBottom: "8px",
                        height: "40px",
                      },
                    },
                  }}
                />
              )}
            />
          </div>

          {/* To Date DatePicker */}
          <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
            <Controller
              name="to_date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="To Date"
                  {...field}
                  onChange={field.onChange}
                  value={field.value || null}
                  format="dd MMM yyyy"
                  minDate={fromDate || null}
                  slotProps={{
                    textField: {
                      variant: "outlined",
                      fullWidth: true,
                      size: "small",
                      sx: {
                        backgroundColor: "white",
                        marginTop: "8px",
                        marginBottom: "8px",
                        height: "40px",
                      },
                    },
                  }}
                />
              )}
            />
          </div>

          {/* Status Select */}
          <div className="col-lg-3 col-md-4 col-sm-6 col-xs-6">
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    backgroundColor: "white",
                    marginTop: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <Autocomplete
                    {...field}
                    options={timesheetStatusOptions}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option.label
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.value === value?.value
                    }
                    value={
                      timesheetStatusOptions.find(
                        (item) => item.value === field.value
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      field.onChange(newValue ? newValue.value : "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Status"
                        size="small"
                        sx={{
                          "& .MuiInputBase-root": {
                            height: 40,
                            padding: 0,
                          },
                          "& .MuiOutlinedInput-input": {
                            padding: "8px 14px",
                          },
                        }}
                      />
                    )}
                  />
                </FormControl>
              )}
            />
          </div>

          {/* Comp-off Status Select */}
          <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
            <Controller
              name="comp_off_status"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    backgroundColor: "white",
                    marginTop: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <Autocomplete
                    {...field}
                    options={compOffStatusOptions}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option.label
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.value === value?.value
                    }
                    value={
                      compOffStatusOptions.find(
                        (item) => item.value === field.value
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      field.onChange(newValue ? newValue.value : "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Compoff Status"
                        size="small"
                        sx={{
                          "& .MuiInputBase-root": {
                            height: 40,
                          },
                        }}
                      />
                    )}
                  />
                </FormControl>
              )}
            />
          </div>

          {/* rm Select */}
          {(isAdminHrRole || isManagerRole) && (
            <>
              <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
                <Controller
                  name="reporting_manager"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      sx={{
                        backgroundColor: "white",
                        marginTop: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      <Autocomplete
                        {...field}
                        options={reportingManagers}
                        getOptionLabel={(option) => {
                          if (typeof option === "string") return option;
                          const name = [
                            option._FirstName,
                            option._MiddleName,
                            option._LastName,
                          ]
                            .filter(Boolean)
                            .join(" ");
                          return name || "";
                        }}
                        isOptionEqualToValue={(option, value) =>
                          option._UserID === value?._UserID
                        }
                        value={field.value || null}
                        onChange={(event, newValue) => {
                          field.onChange(newValue || null);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Reporting Manager"
                            size="small"
                            sx={{
                              "& .MuiInputBase-root": {
                                height: 40,
                              },
                            }}
                          />
                        )}
                      />
                    </FormControl>
                  )}
                />
              </div>
              {/* user status select */}
              <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
                <Controller
                  name="user_status"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      sx={{
                        backgroundColor: "white",
                        marginTop: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      <Autocomplete
                        {...field}
                        options={userStatusOptions}
                        getOptionLabel={(option) =>
                          typeof option === "string" ? option : option.label
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.value === value?.value
                        }
                        value={
                          userStatusOptions.find(
                            (item) => item.value === field.value
                          ) || null
                        }
                        onChange={(event, newValue) => {
                          field.onChange(newValue ? newValue.value : "");
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Employee Status"
                            size="small"
                            sx={{
                              "& .MuiInputBase-root": {
                                height: 40,
                              },
                            }}
                          />
                        )}
                      />
                    </FormControl>
                  )}
                />
              </div>
            </>
          )}

          {/* Filter and Clear Buttons */}
          <div className="col-lg-3 col-md-4 col-sm-12 col-xs-12 mt-2">
            <Button type="submit" variant="success">
              Filter
            </Button>
            <Button
              type="button"
              variant="dark"
              onClick={handleClear}
              style={{ marginLeft: "5px" }}
            >
              Clear
            </Button>
            <Button
              type="button"
              variant="dark"
              style={{ marginLeft: "5px" }}
              onClick={handleExport}
            >
              Export
            </Button>
          </div>
        </div>
      </form>
    </FilterLayout>
  );
};

export default TimesheetFilter;
